import React from 'react';
import { Badge } from '@mantine/core';

export interface AfaBadgeProps {
  /** Color del badge */
  color: 'yellow' | 'orange' | 'lilac' | 'red' | 'blue' | 'green';

  /** Variante del badge (filled o light) */
  variant?: 'filled' | 'light';

  /** Contenido del badge */
  children: React.ReactNode;

  /** Tamaño del badge */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /** Radio de bordes */
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /** Clase CSS adicional */
  className?: string;

  /** Props adicionales para el Badge de Mantine */
  [key: string]: any;
}

/**
 * Badge personalizado de AFA con variantes de color predefinidas
 * 
 * Ejemplos de uso:
 * 
 * <AfaBadge color="green" variant="filled">Finalitzat</AfaBadge>
 * <AfaBadge color="red" variant="light">Rebutjat AFA</AfaBadge>
 * <AfaBadge color="yellow">En revisió</AfaBadge>
 */
export function AfaBadge({
  color,
  variant = 'light',
  children,
  size = 'md',
  radius = 'xl',
  className,
  ...otherProps
}: AfaBadgeProps) {

  // Mapeo de colores y variantes a estilos
  const colorStyles = {
    yellow: {
      light: {
        backgroundColor: '#fffbeb', // yellow-50 mais claro
        color: '#92400e', // yellow-800
      },
      filled: {
        backgroundColor: '#fbbf24', // yellow-400
        color: '#ffffff', // white
      },
    },
    orange: {
      light: {
        backgroundColor: '#fff7ed', // orange-50
        color: '#c2410c', // orange-700
      },
      filled: {
        backgroundColor: '#fb923c', // orange-400
        color: '#ffffff', // white
      },
    },
    lilac: {
      light: {
        backgroundColor: '#faf5ff', // purple-50
        color: '#7c3aed', // purple-600
      },
      filled: {
        backgroundColor: '#a78bfa', // purple-400
        color: '#ffffff', // white
      },
    },
    red: {
      light: {
        backgroundColor: '#fef2f2', // red-50
        color: '#dc2626', // red-600
      },
      filled: {
        backgroundColor: '#f87171', // red-400
        color: '#ffffff', // white
      },
    },
    blue: {
      light: {
        backgroundColor: '#dbeafe', // blue-10
        color: '#2563eb', // blue-600
      },
      filled: {
        backgroundColor: '#60a5fa', // blue-400
        color: '#ffffff', // white
      },
    },
    green: {
      light: {
        backgroundColor: '#f0fdf4', // green-50
        color: '#059669', // green-600
      },
      filled: {
        backgroundColor: '#34d399', // green-400
        color: '#ffffff', // white
      },
    },
  };

  const currentStyle = colorStyles[color][variant];

  // Configuración de tamaños
  const sizeConfig = {
    xs: { fontSize: '10px', padding: '2px 6px' },
    sm: { fontSize: '12px', padding: '4px 8px' },
    md: { fontSize: '13px', padding: '6px 12px' },
    lg: { fontSize: '14px', padding: '8px 16px' },
    xl: { fontSize: '16px', padding: '10px 20px' },
  };

  const currentSize = sizeConfig[size || 'md'];

  return (
    <Badge
      size={size}
      radius={radius}
      {...(className && { className })}
      styles={{
        root: {
          backgroundColor: currentStyle.backgroundColor,
          color: currentStyle.color,
          border: 'none',
          fontWeight: 500,
          textTransform: 'none',
          fontSize: currentSize.fontSize,
          padding: currentSize.padding,
          height: 'auto',
          lineHeight: 1.2,
          display: 'inline-block'
        },
      }}
      {...otherProps}
    >
      {children}
    </Badge>
  );
}
